<script setup lang="ts">
import Simplebar from "simplebar-vue";

import type { TableReportDto } from "@/components/datascience/TableReport.vue";
import TruncatableSpan from "@/components/ui/TruncatableSpan.vue";
import { formatNumber } from "@/services/utils";

const { spec } = defineProps<{
  spec: TableReportDto;
}>();

function formatPercent(proportion: number) {
  if (0.0 < proportion && proportion < 0.001) {
    return "< 0.1%";
  }
  return `${(proportion * 100).toFixed(1)}%`;
}
</script>

<template>
  <div class="statistics">
    <Simplebar class="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Column name</th>
            <th>dtype</th>
            <th>Is sorted</th>
            <th>Null values</th>
            <th>Unique values</th>
            <th>Mean</th>
            <th>Std</th>
            <th>Min</th>
            <th>Median</th>
            <th>Max</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="column in spec.columns" :key="column.name">
            <td>
              <TruncatableSpan :text="column.name" with-end />
            </td>
            <td>{{ column.dtype }}</td>
            <td>{{ column.is_ordered === true ? "true" : "false" }}</td>
            <td class="number">
              {{ column.null_count }} ({{ formatPercent(column.null_proportion) }})
            </td>

            <td v-if="column.n_unique" class="number">
              {{ column.n_unique }} ({{ formatPercent(column.unique_proportion) }})
            </td>
            <td v-else></td>

            <template v-if="column.mean !== undefined">
              <td class="number">
                {{ formatNumber(column.mean) }}
              </td>
              <td class="number">
                {{ formatNumber(column.standard_deviation!) }}
              </td>
            </template>
            <template v-else>
              <td></td>
              <td></td>
            </template>

            <template v-if="column.quantiles !== undefined">
              <td class="number">
                {{ formatNumber(column.quantiles["0.0"]!, { dtype: column.dtype }) }}
              </td>
              <td class="number">
                {{ formatNumber(column.quantiles["0.5"]!, { dtype: column.dtype }) }}
              </td>
              <td class="number">
                {{ formatNumber(column.quantiles["1.0"]!, { dtype: column.dtype }) }}
              </td>
            </template>
            <template v-else-if="column.min !== undefined">
              <td class="number">
                {{ formatNumber(column.min, { dtype: column.dtype }) }}
              </td>
              <td></td>
              <td class="number">
                {{ formatNumber(column.max!, { dtype: column.dtype }) }}
              </td>
            </template>
            <template v-else>
              <td></td>
              <td></td>
              <td></td>
            </template>
          </tr>
        </tbody>
      </table>
    </Simplebar>
  </div>
</template>

<style scoped>
.statistics {
  .table-scroll {
    table {
      border-collapse: separate;
      border-spacing: 0;
      table-layout: fixed;

      &,
      &:focus,
      &:focus-visible {
        outline: none;
      }

      thead {
        tr {
          color: var(--content-secondary);

          th {
            z-index: 1;
            padding: var(--numbers-8);
            border-width: var(--stroke-width-md);
            border-color: var(--border-tertiary);
            border-top-style: solid;
            background-color: var(--background-tertiary);
            text-align: left;
            white-space: preserve nowrap;

            &:first-child {
              position: sticky;
              left: 0;
            }
          }

          &:last-child th {
            border-bottom-style: solid;
          }

          &:first-child th:first-child {
            border-left-style: solid;
            border-top-left-radius: var(--numbers-8);
          }

          &:first-child th:last-child {
            border-right-style: solid;
            border-top-right-radius: var(--numbers-8);
          }
        }
      }

      tbody {
        tr {
          td {
            padding: var(--numbers-8);
            border: var(--stroke-width-md) none var(--border-tertiary);
            border-bottom-style: solid;
            border-left-style: solid;
            text-align: left;
            transition: color var(--animation-duration) var(--animation-easing);

            &:first-child {
              position: sticky;
              left: 0;
              background-color: var(--background-primary);
            }

            &:last-child {
              border-right-style: solid;
            }
          }

          &:last-child td {
            border-bottom-style: solid;
          }

          &:last-child td:first-child {
            border-bottom-left-radius: var(--numbers-8);
          }

          &:last-child td:last-child {
            border-bottom-right-radius: var(--numbers-8);
          }
        }
      }

      th.warning,
      td.warning {
        color: var(--content-negative-bold);
      }
    }
  }
}
</style>
